import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { clampPercentagesToDiyCaps } from './utils/diyRatioCaps';
import './App.css';
import './components/BuilderAction.css';
import teaAPI from './services/api';
import IngredientSelector from './components/IngredientSelector';
import BlendPreview from './components/BlendPreview';
import SizeSelector from './components/SizeSelector';
import PreparationMethodSelector from './components/PreparationMethodSelector';
import LoadingSpinner from './components/LoadingSpinner';
import ErrorMessage from './components/ErrorMessage';
import RecommendedBlends from './components/RecommendedBlends';
import SelectedIngredients from './components/SelectedIngredients';
import SingleIngredientMessage from './components/SingleIngredientMessage';
import HowItWorks from './components/HowItWorks';
import BlendModeToggle from './components/BlendModeToggle';
import BlendInProgressHint from './components/BlendInProgressHint';
import RatioBar from './components/RatioBar';
import RatioJumpHint, { PROPORTIONS_ANCHOR_ID } from './components/RatioJumpHint';
import PreparationChangeConfirmModal from './components/PreparationChangeConfirmModal';
import { useBuilderPhase } from './hooks/useBuilderPhase';
import { BULK_INGREDIENTS_URL } from './config';
import blendProcess from './config/blend-process-content.json';
import { getIncompatibleSelectedIngredients } from './utils/preparationCompatibility';

const VALID_MODES = ['auto', 'user_ratios'];

/**
 * Parse the reorder `percentages` URL fragment into a {id: pct} dict.
 *
 * Two accepted formats:
 *   - CSV: `id1:25,id2:75` (used by App.js-internal reorder URLs)
 *   - JSON: `{"id1":25,"id2":75}` (passed straight from Shopify line-item
 *     `_custom_percentages` properties — easier to URL-encode from Liquid).
 *
 * Silently ignores malformed entries — the validator on add-to-cart catches
 * any drift before the order is placed.
 */
function parsePercentagesParam(raw) {
  if (!raw) return null;
  const trimmed = String(raw).trim();
  let entries = null;

  if (trimmed.startsWith('{')) {
    try {
      const obj = JSON.parse(trimmed);
      if (obj && typeof obj === 'object') {
        entries = Object.entries(obj);
      }
    } catch (_err) {
      // Fall through to CSV parsing.
    }
  }
  if (!entries) {
    entries = trimmed.split(',').map((pair) => pair.split(':'));
  }

  const out = {};
  for (const [id, pct] of entries) {
    const parsed = parseFloat(pct);
    if (id && Number.isFinite(parsed)) {
      out[String(id).trim()] = Math.round(parsed * 10) / 10;
    }
  }
  return Object.keys(out).length ? out : null;
}


function App() {
  // State management
  const [ingredients, setIngredients] = useState([]);
  const [selectedIngredients, setSelectedIngredients] = useState([]);
  const [packetSize, setPacketSize] = useState(100);
  const [preparationMethod, setPreparationMethod] = useState('water');
  const [blendResult, setBlendResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loadingIngredients, setLoadingIngredients] = useState(true);
  // "Choose my ratios" mode + per-ingredient percentages picked by the customer.
  const [mode, setMode] = useState('auto');
  const [customPercentages, setCustomPercentages] = useState({});
  // True once the customer has touched any percentage input (or restored from a
  // URL that pinned specific percentages). While false, we treat the customer
  // as "in autopilot" — adding/removing an ingredient re-fetches engine
  // defaults; once true, new ingredients come in at 0% and the engine no
  // longer second-guesses the customer until they click "Reset to defaults".
  const [userHasEditedRatios, setUserHasEditedRatios] = useState(false);
  const [prepChangePending, setPrepChangePending] = useState(null);
  const evaluateAbortRef = useRef(null);
  const latestEvaluateRequestRef = useRef(0);
  const { builderPhaseAnim } = useBuilderPhase(selectedIngredients);
  // Load ingredients on mount, then apply URL params (reorder / cart "edit blend" link)
  // Auto mode:        ?ingredients=id1,id2,id3&size=100|200|500&preparation=water|milk|both
  // User-ratios mode: same, plus &mode=user_ratios&percentages=id1:25,id2:75
  useEffect(() => {
    loadIngredients().then((catalog) => {
      const params = new URLSearchParams(window.location.search);
      const ingredientParam = params.get('ingredients');
      const sizeParam = params.get('size');
      const preparationParam = params.get('preparation');
      const modeParam = params.get('mode');
      const percentagesParam = params.get('percentages');

      if (ingredientParam) {
        const ids = ingredientParam.split(',').map((id) => id.trim()).filter(Boolean);
        if (ids.length > 0) {
          setSelectedIngredients(ids);
        }
      }
      if (sizeParam) {
        const size = parseInt(sizeParam, 10);
        if ([100, 200, 500].includes(size)) {
          setPacketSize(size);
        }
      }
      if (preparationParam && ['water', 'milk', 'both'].includes(preparationParam)) {
        setPreparationMethod(preparationParam);
      }
      if (modeParam && VALID_MODES.includes(modeParam)) {
        setMode(modeParam);
        if (modeParam === 'user_ratios') {
          const parsed = parsePercentagesParam(percentagesParam);
          if (parsed) {
            // Clamp to DIY hard caps so crafted/stale links don't 400 on
            // evaluate. If clamping breaks the 100% sum, existing ratio UI
            // asks the customer to rebalance — no hard error page.
            const { percentages } = clampPercentagesToDiyCaps(
              parsed,
              catalog || [],
            );
            setCustomPercentages(percentages);
            setUserHasEditedRatios(true);
          }
          // No percentages in URL: leave userHasEditedRatios=false so the
          // engine-defaults effect populates customPercentages once
          // ingredients/preparation settle.
        }
      }
    });
  }, []);

  // In user_ratios mode, only call the API when the percentages cleanly sum to
  // 100% — otherwise we'd noisy the backend with 400s while the customer is
  // still adjusting. Memoized so the effect can use it as a stable trigger.
  const userRatiosReady = useMemo(() => {
    if (mode !== 'user_ratios') return false;
    if (selectedIngredients.length < 2) return false;
    if (selectedIngredients.some((id) => !(Number(customPercentages[id]) > 0))) return false;
    const total = selectedIngredients.reduce(
      (sum, id) => sum + Number(customPercentages[id] || 0),
      0,
    );
    return Math.abs(total - 100) <= 0.1;
  }, [mode, selectedIngredients, customPercentages]);

  // Shared error/cleanup helper so both effects below handle network failures,
  // rate limits, and cancellations identically.
  const applyEvaluateError = useCallback((err) => {
    if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return;
    if (err.response?.status === 429) {
      const retryAfter = err.response.headers['retry-after'];
      if (retryAfter) {
        setError(`Please wait ${retryAfter} seconds before trying another blend.`);
      } else {
        setError('Please wait a moment before trying another blend.');
      }
    } else if (
      err.response?.status === 400 &&
      typeof err.response.data?.detail === 'string' &&
      err.response.data.detail.length > 0
    ) {
      // Validation rejections (e.g. caution-group budgets) carry customer-
      // voiced copy explaining exactly what to adjust — show it verbatim.
      setError(err.response.data.detail);
    } else {
      setError('Failed to evaluate blend. Please try again.');
    }
    console.error('Error evaluating blend:', err);
    setBlendResult(null);
  }, []);

  // Effect A — Engine-defaults path for "Choose my ratios" when the customer
  // hasn't touched the percentages yet. Calls the API in auto mode and adopts
  // the engine's proportions as the seeded values. Deliberately doesn't depend
  // on `customPercentages` (it WRITES to it) so it doesn't loop.
  useEffect(() => {
    if (mode !== 'user_ratios' || userHasEditedRatios) return;
    if (selectedIngredients.length < 2) {
      setBlendResult(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    latestEvaluateRequestRef.current += 1;
    const requestId = latestEvaluateRequestRef.current;

    const timeoutId = setTimeout(async () => {
      if (evaluateAbortRef.current) evaluateAbortRef.current.abort();
      const controller = new AbortController();
      evaluateAbortRef.current = controller;

      try {
        const result = await teaAPI.evaluateBlend(
          selectedIngredients, packetSize, preparationMethod, controller.signal,
          { mode: 'auto' },
        );
        if (latestEvaluateRequestRef.current !== requestId) return;
        setBlendResult(result);
        if (result && result.custom_percentages) {
          setCustomPercentages(result.custom_percentages);
        }
      } catch (err) {
        if (latestEvaluateRequestRef.current !== requestId) return;
        applyEvaluateError(err);
      } finally {
        if (latestEvaluateRequestRef.current === requestId) setLoading(false);
      }
    }, 600);

    return () => {
      clearTimeout(timeoutId);
      if (evaluateAbortRef.current) evaluateAbortRef.current.abort();
    };
  }, [mode, userHasEditedRatios, selectedIngredients, packetSize, preparationMethod, applyEvaluateError]);

  // Effect B — Regular eval for auto mode and for user_ratios after the
  // customer has edited (and reached a valid 100% sum). Skips the
  // not-yet-edited user_ratios case entirely; Effect A owns that.
  useEffect(() => {
    if (selectedIngredients.length < 2) {
      setBlendResult(null);
      setLoading(false);
      return;
    }
    if (mode === 'user_ratios' && !userHasEditedRatios) {
      return; // Effect A handles this
    }
    if (mode === 'user_ratios' && !userRatiosReady) {
      setBlendResult(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    latestEvaluateRequestRef.current += 1;
    const requestId = latestEvaluateRequestRef.current;

    const timeoutId = setTimeout(async () => {
      if (evaluateAbortRef.current) evaluateAbortRef.current.abort();
      const controller = new AbortController();
      evaluateAbortRef.current = controller;

      try {
        const result = await teaAPI.evaluateBlend(
          selectedIngredients, packetSize, preparationMethod, controller.signal,
          {
            mode,
            customPercentages: mode === 'user_ratios' ? customPercentages : null,
          },
        );
        if (latestEvaluateRequestRef.current !== requestId) return;
        setBlendResult(result);
      } catch (err) {
        if (latestEvaluateRequestRef.current !== requestId) return;
        applyEvaluateError(err);
      } finally {
        if (latestEvaluateRequestRef.current === requestId) setLoading(false);
      }
    }, 600);

    return () => {
      clearTimeout(timeoutId);
      if (evaluateAbortRef.current) evaluateAbortRef.current.abort();
    };
  }, [
    selectedIngredients, packetSize, preparationMethod, mode,
    userHasEditedRatios, userRatiosReady, customPercentages, applyEvaluateError,
  ]);

  const loadIngredients = async () => {
    try {
      setLoadingIngredients(true);
      const data = await teaAPI.getIngredients(false, true); // Custom-blend eligible ingredients only
      setIngredients(data);
      setError(null);
      return data;
    } catch (err) {
      // Handle rate limiting
      if (err.response?.status === 429) {
        const retryAfter = err.response.headers['retry-after'];
        if (retryAfter) {
          setError(`Too many requests. Please wait ${retryAfter} seconds.`);
        } else {
          setError('Too many requests. Please wait a moment and try again.');
        }
      } else {
        setError("We couldn't load ingredients. Please try again.");
      }
      console.error('Error loading ingredients:', err);
      return [];
    } finally {
      setLoadingIngredients(false);
    }
  };

  const handleIngredientToggle = useCallback((ingredientId) => {
    setSelectedIngredients((prev) => {
      const next = prev.includes(ingredientId)
        ? prev.filter((id) => id !== ingredientId)
        : [...prev, ingredientId];

      // Keep customPercentages in sync with the selection. We never auto-fill
      // a new ingredient's % — the customer is designing the blend; nudging
      // them to set a value is part of the experience. Removing an ingredient
      // simply drops its entry; the running total then deviates from 100 and
      // the bar surfaces that to the customer.
      setCustomPercentages((current) => {
        const updated = { ...current };
        for (const key of Object.keys(updated)) {
          if (!next.includes(key)) delete updated[key];
        }
        return updated;
      });

      return next;
    });
  }, []);

  const handleQualityChange = useCallback((fromId, toId) => {
    if (!fromId || !toId || fromId === toId) return;
    setSelectedIngredients((prev) => {
      if (!prev.includes(fromId)) return prev;
      const next = [];
      const seen = new Set();
      for (const id of prev) {
        const mapped = id === fromId ? toId : id;
        if (seen.has(mapped)) continue;
        seen.add(mapped);
        next.push(mapped);
      }

      setCustomPercentages((current) => {
        const updated = { ...current };
        if (Object.prototype.hasOwnProperty.call(updated, fromId)) {
          updated[toId] = updated[fromId];
          delete updated[fromId];
        }
        for (const key of Object.keys(updated)) {
          if (!next.includes(key)) delete updated[key];
        }
        return updated;
      });

      return next;
    });
  }, []);

  const handlePercentageChange = useCallback((ingredientId, percent) => {
    setCustomPercentages((prev) => ({ ...prev, [ingredientId]: percent }));
    // Any edit takes the customer off autopilot — from here, new ingredients
    // come in at 0% and the engine no longer rebalances on add/remove.
    setUserHasEditedRatios(true);
  }, []);

  const handleModeChange = useCallback((nextMode) => {
    if (!VALID_MODES.includes(nextMode) || nextMode === mode) return;
    if (nextMode === 'user_ratios') {
      // Re-arm autopilot — Effect A will fetch engine defaults and adopt
      // them as the seeded percentages.
      setUserHasEditedRatios(false);
    }
    setMode(nextMode);
  }, [mode]);

  const handleResetToDefaults = useCallback(() => {
    // Clearing the edit flag re-triggers Effect A which refetches the engine
    // defaults and overwrites whatever the customer had.
    setUserHasEditedRatios(false);
  }, []);

  const clearBlendSelection = useCallback(() => {
    setSelectedIngredients([]);
    setCustomPercentages({});
    setUserHasEditedRatios(false);
    setBlendResult(null);
  }, []);

  const handlePreparationMethodChange = useCallback((nextMethod) => {
    if (nextMethod === preparationMethod) return;

    if (selectedIngredients.length === 0) {
      setPreparationMethod(nextMethod);
      return;
    }

    const incompatible = getIncompatibleSelectedIngredients(
      ingredients,
      selectedIngredients,
      nextMethod,
    );

    if (incompatible.length === 0) {
      setPreparationMethod(nextMethod);
      return;
    }

    setPrepChangePending({
      method: nextMethod,
      incompatibleNames: incompatible.map((ing) => ing.name),
    });
  }, [preparationMethod, selectedIngredients, ingredients]);

  const handlePrepChangeConfirm = useCallback(() => {
    if (!prepChangePending) return;
    clearBlendSelection();
    setPreparationMethod(prepChangePending.method);
    setPrepChangePending(null);
  }, [prepChangePending, clearBlendSelection]);

  const handlePrepChangeCancel = useCallback(() => {
    setPrepChangePending(null);
  }, []);

  const customPercentagesForApi = useMemo(() => {
    if (mode !== 'user_ratios') return null;
    const out = {};
    for (const id of selectedIngredients) {
      const v = Number(customPercentages[id]);
      if (Number.isFinite(v)) out[id] = v;
    }
    return out;
  }, [mode, selectedIngredients, customPercentages]);

  // Sum across selected ingredients — drives the in-progress placeholder.
  const userRatiosTotal = useMemo(() => {
    if (mode !== 'user_ratios') return 0;
    return selectedIngredients.reduce(
      (sum, id) => sum + (Number(customPercentages[id]) || 0),
      0,
    );
  }, [mode, selectedIngredients, customPercentages]);

  // Names of selected ingredients still sitting at 0%. The in-progress
  // hint uses these to surface "give it a percentage or remove it" copy
  // when the customer's sum reaches 100 but a slot is still blank.
  const userRatiosZeroNames = useMemo(() => {
    if (mode !== 'user_ratios') return [];
    return selectedIngredients
      .filter((id) => !(Number(customPercentages[id]) > 0))
      .map((id) => {
        const ing = ingredients.find((i) => i.id === id);
        return (ing?.name || id).replace(/_/g, ' ');
      });
  }, [mode, selectedIngredients, customPercentages, ingredients]);

  return (
    <div className="app">
      <header className="app-header app-header--with-journey">
        <div className="app-header__inner">
          <h1>{blendProcess.hero.heading}</h1>
          <p className="tagline paragraph">{blendProcess.hero.body}</p>
          <HowItWorks />
        </div>
      </header>

      <main className="app-main">
        {error && <ErrorMessage message={error} onRetry={loadIngredients} />}

        <div className="layout-container">
          {/* Left Sidebar - Sticky */}
          <aside className="sidebar-left">
            {/* Selected Ingredients Summary — anchor target for the
                mobile "↓ set proportions" jump hint rendered below the
                picker in user_ratios mode. */}
            {selectedIngredients.length > 0 && (
              <div
                id={PROPORTIONS_ANCHOR_ID}
                className="sidebar-slot sidebar-slot--proportions"
                style={{ scrollMarginTop: '1rem' }}
              >
                <SelectedIngredients
                  selectedIngredients={selectedIngredients}
                  ingredients={ingredients}
                  onRemove={handleIngredientToggle}
                  mode={mode}
                  customPercentages={customPercentages}
                  onPercentageChange={handlePercentageChange}
                />
              </div>
            )}

            {/* Ratio visualization — only in user_ratios mode */}
            {mode === 'user_ratios' && selectedIngredients.length >= 2 && (
              <section className="sidebar-section sidebar-slot sidebar-slot--ratiobar">
                <h2 className="sidebar-title">Your proportions</h2>
                <RatioBar
                  selectedIngredients={selectedIngredients}
                  ingredients={ingredients}
                  customPercentages={customPercentages}
                  onResetToDefaults={handleResetToDefaults}
                  canResetToDefaults={userHasEditedRatios}
                />
              </section>
            )}

            {/* Preparation Method — stays at the top of the page on mobile
                (visual order via CSS) since it affects which ingredients
                appear in the picker. */}
            <section className="sidebar-section sidebar-section--choices sidebar-slot sidebar-slot--prep">
              <h2 className="sidebar-title">Preparation</h2>
              <p className="sidebar-lead">How you&apos;ll brew this blend</p>
              <PreparationMethodSelector
                selected={preparationMethod}
                onChange={handlePreparationMethodChange}
              />
            </section>

            {/* Size Selection */}
            {selectedIngredients.length >= 2 && (
              <section className="sidebar-section sidebar-section--choices sidebar-slot sidebar-slot--size">
                <h2 className="sidebar-title">Packet size</h2>
                <p className="sidebar-lead">Hand-blended pouch, sealed for your cup</p>
                <SizeSelector
                  selected={packetSize}
                  onChange={setPacketSize}
                  cupsBySize={blendResult?.cups_by_size ?? null}
                />
              </section>
            )}
          </aside>

          {/* Main Content - Center — stack is tall so the sticky rail stays in view for the whole column */}
          <div
            className={`main-content${builderPhaseAnim ? ' main-content--phase-shift' : ''}`}
          >
            <div className="main-content__stack">
              <div className="main-content__panels">
              {/* Select Ingredients */}
              <section className="content-section main-content-compose">
                <h2 className="content-title">Compose your blend</h2>

                <BlendModeToggle mode={mode} onChange={handleModeChange} />

                {loadingIngredients ? (
                  <LoadingSpinner message="Loading ingredients..." />
                ) : (
                  <IngredientSelector
                    ingredients={ingredients}
                    selectedIngredients={selectedIngredients}
                    onToggle={handleIngredientToggle}
                    onQualityChange={handleQualityChange}
                    preparationMethod={preparationMethod}
                  />
                )}

                {/* Mobile-only hint: when in user_ratios mode and the
                    customer has picked at least one ingredient, point them
                    to the proportions panel further down the page. Hidden
                    on desktop where the panel is in the sidebar. */}
                {mode === 'user_ratios' && selectedIngredients.length >= 1 && (
                  <RatioJumpHint count={selectedIngredients.length} />
                )}
              </section>

              {/* Single ingredient nudge — visible when only 1 picked. */}
              {selectedIngredients.length === 1 && (
                <section className="content-section main-content-single-ingredient">
                  <SingleIngredientMessage bulkIngredientUrl={BULK_INGREDIENTS_URL} />
                </section>
              )}

              {/* Result slot: holds loading, in-progress placeholder, or the
                  final preview + cart. Wrapped in a single container so on
                  mobile it can be reordered to land below the proportion /
                  size blocks. */}
              <div className="main-content-result-slot">
                {loading && selectedIngredients.length >= 2 && (
                  <LoadingSpinner message="Calculating your perfect blend..." />
                )}

                {/* While the customer is still adjusting their ratios in
                    user_ratios mode and the sum hasn't hit 100% yet, the
                    evaluate API isn't called — we show a friendly progress
                    placeholder in the result slot so the panel never goes
                    empty. */}
                {!loading
                  && mode === 'user_ratios'
                  && selectedIngredients.length >= 2
                  && !userRatiosReady && (
                  <section className="content-section blend-offer-section">
                    <p className="blend-offer__eyebrow">Your blend</p>
                    <h2 className="blend-offer__title">Your Custom Blend</h2>
                    <BlendInProgressHint
                      total={userRatiosTotal}
                      zeroIngredientNames={userRatiosZeroNames}
                    />
                  </section>
                )}

                {blendResult && !loading && (
                  <BlendPreview
                    result={blendResult}
                    packetSize={packetSize}
                    selectedIngredients={selectedIngredients}
                    preparationMethod={preparationMethod}
                    ingredients={ingredients}
                    mode={mode}
                    customPercentages={customPercentagesForApi}
                  />
                )}
              </div>
              </div>
            </div>
          </div>

        </div>

        {blendResult && !loading && blendResult.success && blendResult.matched_recipes && blendResult.matched_recipes.length > 0 && (
          <div className="recommendations-footer">
            <RecommendedBlends recommendations={blendResult.matched_recipes} />
          </div>
        )}
      </main>

      {prepChangePending && (
        <PreparationChangeConfirmModal
          targetMethod={prepChangePending.method}
          currentMethod={preparationMethod}
          incompatibleNames={prepChangePending.incompatibleNames}
          onConfirm={handlePrepChangeConfirm}
          onCancel={handlePrepChangeCancel}
        />
      )}
    </div>
  );
}

export default App;
